/**
 * The taxpayer's portal session — one OTP, six hours, shared by everything.
 * @file src/web/features/admin/components/PortalSessionPanel.tsx
 *
 * GSTN authenticates the TAXPAYER, not the firm, so anything that touches the
 * portal — filing a GSTR-1 or 3B, pulling a 2B — needs the client to read out
 * one code. The session is stored per registration and lasts six hours, so the
 * client is rung once and the rest of the work happens without them.
 *
 * It lives here rather than inside the filing panel because the 2B tab needs
 * it too: a Fetch button that can only say "no session" with no way to open
 * one is a dead end.
 */

import { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertTriangle, Smartphone, ShieldCheck } from 'lucide-react';
import { adminFetchJson } from '../lib/admin-api';
import { typedOtp, isOtp } from '../../../shared/utils/otp';

export interface SessionInfo {
  ok: boolean; error?: string;
  configured?: boolean; testEnvironment?: boolean;
  gstin?: string; portalUsername?: string | null; usernameMissing?: boolean;
  session?: { openedAt: string; expiresAt: string; minutesLeft: number } | null;
  /** Who signs by EVC (PAN masked): the signatory in Masters, or — for a
   *  proprietorship with none recorded — the proprietor, from the GSTIN.
   *  Null when nobody can. */
  signatory?: { name: string; pan: string; fromGstin?: boolean } | null;
}

/** The id the screen's one session panel is rendered under, so anything that
 *  needs a session can send the user to it rather than describe where it is. */
export const PORTAL_SESSION_ANCHOR = 'portal-session';

/**
 * Scroll to that panel and put the cursor in the OTP box.
 *
 * Reached from the filing steps, which cannot open a session themselves — one
 * session serves the whole screen, so a second OTP form would either duplicate
 * this one or contradict it. Telling someone the panel is "at the top" still
 * leaves them looking for it; this lands them in the field.
 */
export function focusPortalSession() {
  const el = document.getElementById(PORTAL_SESSION_ANCHOR);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  // A moment's ring, so it is obvious what the page just moved to.
  el.classList.add('ring-2', 'ring-highlighter');
  window.setTimeout(() => el.classList.remove('ring-2', 'ring-highlighter'), 1600);
  window.setTimeout(() => el.querySelector('input')?.focus(), 350);
}

export function PortalSessionPanel({ entityId, gstRegistrationId, heading = 'Portal session', purpose, onStatus }: {
  entityId: string | null;
  gstRegistrationId?: string | null;
  heading?: string;
  /** Why this screen needs the session — said plainly, since it costs a phone call. */
  purpose?: string;
  onStatus?: (info: SessionInfo) => void;
}) {
  const [info, setInfo] = useState<SessionInfo | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [otp, setOtp] = useState('');
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  const q = { entityId: entityId ?? undefined, gstRegistrationId: gstRegistrationId ?? undefined };

  const load = useCallback(() => {
    adminFetchJson<SessionInfo>('/api/gst/portal/session', { query: q })
      .then((i) => { setInfo(i); onStatus?.(i); })
      .catch((e) => {
        const i = { ok: false, error: e instanceof Error ? e.message : 'Failed' };
        setInfo(i); onStatus?.(i);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, gstRegistrationId]);
  useEffect(load, [load]);

  async function run(name: string, fn: () => Promise<any>) {
    setBusy(name); setMsg(null);
    try {
      const r = await fn();
      if (r?.ok === false) { setMsg({ tone: 'err', text: r.error ?? 'Failed' }); return null; }
      return r;
    } catch (e) {
      setMsg({ tone: 'err', text: e instanceof Error ? e.message.replace(/^\d+\s/, '') : 'Failed' });
      return null;
    } finally { setBusy(null); }
  }

  const sendOtp = () => run('otp', async () => {
    const r = await adminFetchJson<any>('/api/gst/portal/otp', { method: 'POST', query: q });
    if (r?.ok !== false) setMsg({ tone: 'ok', text: r.message ?? 'OTP sent to the client.' });
    return r;
  });

  const verify = () => run('verify', async () => {
    const r = await adminFetchJson<any>('/api/gst/portal/verify', { method: 'POST', query: q, body: JSON.stringify({ otp }) });
    if (r?.ok !== false) { setOtp(''); setMsg({ tone: 'ok', text: 'Session open for 6 hours.' }); load(); }
    return r;
  });

  if (!info) return <div className="rounded-xl border border-ink-200 bg-paper px-3 py-2 text-xs text-ink-400">Checking portal session…</div>;

  if (info.configured === false) {
    return (
      <div className="rounded-xl border border-ink-200 bg-ink-50/60 px-3 py-2 text-[11px] text-ink-600">
        No GSP credentials, so nothing here reaches the portal — set{' '}
        <code className="font-mono">SANDBOX_GST_API_KEY</code> and{' '}
        <code className="font-mono">SANDBOX_GST_API_SECRET</code>.
      </div>
    );
  }

  const live = !!info.session;

  /* One row, because that is all it is: a session is either open or it is not,
     and the way to open it is two controls wide. It sits above every tab, so
     anything taller than a line pushes the actual work down the page on every
     screen — including the tabs that need no session at all. Warnings appear
     only when they apply, and a blocker gets its own line; nothing else does. */
  return (
    <div className="rounded-xl border border-ink-200 bg-paper px-3 py-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-ink-600">
          <Smartphone className="h-3.5 w-3.5 text-ink-400" /> {heading}
        </span>

        {live ? (
          <>
            <span className="inline-flex items-center gap-1 rounded-full border border-ok-200 bg-ok-50 px-2 py-0.5 text-[11px] font-medium text-ok-700">
              <ShieldCheck className="h-3 w-3" /> Open · {info.session!.minutesLeft} min left
            </span>
            {purpose && <span className="text-[11px] text-ink-400">{purpose}</span>}
          </>
        ) : (
          <>
            <button onClick={sendOtp} disabled={!!busy || info.usernameMissing}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-300 bg-paper px-2.5 py-1 text-[11px] font-semibold text-ink-700 hover:bg-ink-50 disabled:opacity-50">
              {busy === 'otp' && <Loader2 className="h-3 w-3 animate-spin" />} Send OTP
            </button>
            <input
              value={otp}
              onChange={(e) => setOtp(typedOtp(e.target.value))}
              onKeyDown={(e) => { if (e.key === 'Enter' && isOtp(otp)) verify(); }}
              placeholder="OTP"
              autoComplete="one-time-code"
              autoCapitalize="characters"
              spellCheck={false}
              maxLength={6}
              className="w-24 rounded-lg border border-ink-300 px-2 py-1 text-xs tabular-nums focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter"
            />
            <button onClick={verify} disabled={!!busy || !isOtp(otp)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-inkblack px-2.5 py-1 text-[11px] font-semibold text-white disabled:opacity-50">
              {busy === 'verify' && <Loader2 className="h-3 w-3 animate-spin" />} Open
            </button>
            {purpose && <span className="text-[11px] text-ink-400">{purpose}</span>}
          </>
        )}

        {/* A chip, not a banner. It is true on every dev machine and reading a
            two-line warning above every GST screen teaches people to skip it. */}
        {info.testEnvironment && (
          <span
            title="Calls go to Sandbox's fixture host and reach no real GST portal."
            className="inline-flex items-center gap-1 rounded-full border border-warn-200 bg-warn-50 px-2 py-0.5 text-[11px] font-medium text-warn-700"
          >
            <AlertTriangle className="h-3 w-3" /> Test portal
          </span>
        )}

        {msg && (
          <span className={`text-[11px] ${msg.tone === 'ok' ? 'text-ok-700' : 'text-danger-700'}`}>{msg.text}</span>
        )}
      </div>

      {/* Its own line: this one stops the OTP being sent at all, and the fix is
          on another screen. */}
      {info.usernameMissing && (
        <p className="mt-1.5 flex items-start gap-1.5 text-[11px] text-danger-700">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          No portal username for {info.gstin ?? 'this GSTIN'} — add it in Masters → Entity → Statutory registrations before sending an OTP.
        </p>
      )}
    </div>
  );
}
